import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ModalShell } from "@/components/ui/modal-shell";
import { nomeDaMesa } from "@/lib/mesa";

// «Ler QR code» das Comandas abertas (Pedro, 29/10/2026): a câmera lê o QR da mesa e a conta abre para fechar. A
// biblioteca (qr-scanner, que lê também no iPhone, onde o navegador não tem leitor próprio) só baixa quando alguém
// toca no botão. `onLido` devolve o erro de um QR que não serve (de outra loja, ou um código qualquer): a câmera segue
// aberta com o erro embaixo, para o funcionário apontar para o QR certo.
export function LerQrDaMesa({ onLido, onClose, porCliente = false }: { onLido: (texto: string) => Promise<string | null>; onClose: () => void; porCliente?: boolean }) {
  const video = useRef<HTMLVideoElement>(null);
  const [abrindo, setAbrindo] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const lido = useRef(onLido);
  lido.current = onLido;

  useEffect(() => {
    let vivo = true;
    let conferindo = false;
    let recusado: string | null = null;
    let scanner: { start(): Promise<void>; destroy(): void } | null = null;
    (async () => {
      try {
        const { default: QrScanner } = await import("qr-scanner");
        if (!vivo || !video.current) return;
        const s = new QrScanner(
          video.current,
          (r) => {
            // O mesmo QR recusado continua na frente da câmera: não pergunta de novo.
            if (!vivo || conferindo || r.data === recusado) return;
            conferindo = true;
            void lido.current(r.data).then((motivo) => {
              conferindo = false;
              if (!vivo || !motivo) return;
              recusado = r.data;
              setErro(motivo);
            });
          },
          { returnDetailedScanResult: true, preferredCamera: "environment", highlightScanRegion: true, maxScansPerSecond: 8 },
        );
        scanner = s;
        await s.start();
        if (vivo) setAbrindo(false);
      } catch (e) {
        if (!vivo) return;
        setAbrindo(false);
        const t = String((e as { name?: string })?.name ?? e);
        setErro(
          /NotAllowed|Permission/i.test(t)
            ? "A câmera está bloqueada para este site. Libere a câmera nas permissões do navegador e toque em «Ler QR code» de novo."
            : /not found|NotFound|Overconstrained/i.test(t)
              ? "Este aparelho não tem câmera."
              : "Não foi possível abrir a câmera.",
        );
      }
    })();
    return () => {
      vivo = false;
      scanner?.destroy();
    };
  }, []);

  return (
    <ModalShell
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title="Ler QR code"
      subtitle={`Aponte a câmera para o QR code da ${nomeDaMesa(porCliente)}.`}
      size="sm"
      footer={
        <Button type="button" variant="ghost" className="w-full" onClick={onClose}>
          Cancelar
        </Button>
      }
    >
      <div className="relative overflow-hidden rounded-lg bg-black aspect-square max-w-full" data-testid="pdv-ler-qr-camera">
        <video ref={video} className="h-full w-full object-cover" muted playsInline />
        {abrindo && !erro && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-white/80" />
          </div>
        )}
      </div>
      {erro && (
        <p className="mt-3 text-sm text-destructive" data-testid="pdv-ler-qr-erro">
          {erro}
        </p>
      )}
    </ModalShell>
  );
}
